import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import { faEllipsisVertical, faAngleDown, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';

import { SampleConfigurationSet } from '../sampleSets.ts';
import { BeamlineContext } from '../beamlineProvider.tsx';
import { SampleSetsContext, SampleSetsLoadingState } from '../sampleSetsProvider.tsx';
import { LoadingBanner, LoadingState } from '../components/loadingBanner.tsx';
import { Coordinates, SortableTable } from '../components/sortableTable.tsx';
import BeamlineHeader from '../components/beamlineHeader.tsx';
import AddSets from './addSets.tsx';
import DeleteSets from './deleteSets.tsx';
import ArchiveEsaf from './archiveEsaf.tsx';
import EsafAsJson from './esafAsJson.tsx';
import { timestampToTodayString, truthyJoin, sortWithNumberParsing } from '../utils.tsx';
import './esafHome.css';


// An elaboration of the different columns we can expect in the Set table.
enum SetField {
  Slug, Name, Description, Samples, LastModified
};
const setFieldDisplayOrder: SetField[] = [
  SetField.Slug, SetField.Name, SetField.Description, SetField.Samples, SetField.LastModified
];
const columnNames = [
  "Id", "Name", "Description", "Samples", "Last Modified"
];


const EsafHome: React.FC = () => {

  var { proposalId } = useParams();
  proposalId = proposalId || "";

  const beamlineContext = useContext(BeamlineContext);
  const sampleSetsContext = useContext(SampleSetsContext);

  const [loading, setLoading] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("");

  const [sortedSetSlugs, setSortedSetSlugs] = useState<string[]>([]);
  const [selectedSetSlugs, setSelectedSetSlugs] = useState<string[]>([]);
  // We have one column hidden by default
  const [hiddenColumnSet, setHiddenColumnSet] = useState<Set<number>>(new Set([SetField.Slug]));

  const [tableSortColumn, setTableSortColumn] = useState<SetField>(SetField.Name);
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(false);


  useEffect(() => {
    if (sampleSetsContext.loadingState == SampleSetsLoadingState.Failed) {
      setLoading(LoadingState.Failure);
      setLoadingMessage("Failed to load Sets. Are you sure you're still logged in?");
      return;
    }
    if (sampleSetsContext.loadingState == SampleSetsLoadingState.Succeeded) {
      setSortedSetSlugs(sortSetSlugs(tableSortColumn, tableSortReverse));
      setLoading(LoadingState.Success);
      return;
    }
    setLoading(LoadingState.Loading);
    setLoadingMessage("Loading Sets...");
  }, [sampleSetsContext.changeCounter, sampleSetsContext.loadingState]);


  function sortSetSlugs(field: SetField, reverse: boolean): string[] {
    const sets = Array.from(sampleSetsContext.sets.setsBySlug.values());
    var sorted:SampleConfigurationSet[] = [];
    switch (field) {
      case SetField.Slug:
        sorted = sets.sort((a, b) => { return sortWithNumberParsing(a.slug, b.slug, reverse)});
        break;
      case SetField.Name:
        sorted = sets.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        break;
      case SetField.Description:
        sorted = sets.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        break;
      case SetField.Samples:
        sorted = sets.sort((a, b) => {
          const aCount = a.samplesBySlug.size;
          const bCount = b.samplesBySlug.size;
          return reverse ? bCount - aCount : aCount - bCount;
        });
        break;
      case SetField.LastModified:
        sorted = sets.sort((a, b) => {
          const aDate = Date.parse(a.dateLastModified);
          const bDate = Date.parse(b.dateLastModified);
          return reverse ? bDate - aDate : aDate - bDate;
        });
        break;
    }
    return sorted.map((s) => s.slug);
  }


  function clickedTableSortHeader(f: SetField) {
    const sortDirection = (f == tableSortColumn) ? !tableSortReverse : tableSortReverse;
    setTableSortColumn(f);
    setTableSortReverse(sortDirection);
    setSortedSetSlugs(sortSetSlugs(f, sortDirection));
  }


  function toggleHiddenColumn(index: number) {
    const newHiddenColumnSet = new Set(hiddenColumnSet);
    if (hiddenColumnSet.has(index)) {
      newHiddenColumnSet.delete(index);
    } else {
      newHiddenColumnSet.add(index);
    }
    setHiddenColumnSet(newHiddenColumnSet);
  }


  function tableRowSelectionChanged(identifiers: string[]) {
    setSelectedSetSlugs(identifiers);
  }


  function renderHeaderCell(x: number): React.JSX.Element {
    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    const fieldType = setFieldDisplayOrder[x];
    const fieldName = columnNames[x];

    return (<th key={fieldName}
            className={truthyJoin("sortable", (tableSortColumn == fieldType) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader(fieldType) }
            scope="col">{fieldName}</th>);
  }


  // Function provided to the SortableTable.  Given coordinates, renders an editable cell.
  // The editable cell is given the activationStatus and cellFunctions prepared by the EditableTable.
  function renderTableCell(coordinates: Coordinates): React.JSX.Element {
    const thisSet = sampleSetsContext.sets.setsBySlug.get(sortedSetSlugs[coordinates.y]);
    if (!thisSet) {
      console.error("Could not find set for slug " + sortedSetSlugs[coordinates.y]);
      return (<></>);
    }

    // Set the value for the cell, making any conversions needed, and apply additional per-column settings.
    switch (setFieldDisplayOrder[coordinates.x]) {
      case SetField.Slug:
        return (<>{thisSet.slug}</>);
      case SetField.Name:
        return (<><Link to={ "set/" + thisSet.slug }>{ thisSet.name }</Link></>);
      case SetField.Description:
        return (<>{thisSet.description ?? ""}</>);
      case SetField.Samples:
        return (<>{thisSet.samplesBySlug.size}</>);
      case SetField.LastModified:
        return (<>{timestampToTodayString(Date.parse(thisSet.dateLastModified) / 1000)}</>);
      default:
        return (<></>);
      }
  }


  const sortableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    renderTableCell: renderTableCell,
    rowSelectionChanged: tableRowSelectionChanged
  };


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loading != LoadingState.Success) {
    return (<LoadingBanner state={loading} message={loadingMessage}></LoadingBanner>)
  }

  const sets = sampleSetsContext.sets.getSortedSets();

  return (
    <>
      <BeamlineHeader />

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <nav className="breadcrumb is-medium" aria-label="breadcrumbs">
              <ul>
                <li><Link to={ "/" }>ESAFs</Link></li>
                <li className="is-active"><Link to={ "/esaf/" + sampleSetsContext.esaf!.slug }>{ sampleSetsContext.esaf!.name }</Link></li>
              </ul>
            </nav>
          </div>
          { sampleSetsContext.esaf!.archived && (
            <div className="level-item">
              <p className="subtitle is-5" style={{ "color": "darkred" }}>(This ESAF is archived.)</p>
             </div>
          )}
        </div>
        <div className="level-right">
          <div className="level-item">

            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button" aria-haspopup="true" aria-controls="dropdown-menu-howto">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-howto" role="menu">
                <div className="dropdown-content">
                  <ArchiveEsaf />
                  <EsafAsJson />
                </div>
              </div>
            </div>

          </div>
        </div>
      </nav>

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5"><strong>{ sortedSetSlugs.length }</strong> { beamlineContext.setNounPlural }.</p>
          </div>
          <div className="level-item">
            <AddSets />
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button is-small is-ghost" aria-haspopup="true" aria-controls="dropdown-menu-columns">
                  <p className="subtitle is-6">Columns</p>
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-columns" role="menu">
                <div className="dropdown-content">
                  { setFieldDisplayOrder.map((_, index) => {
                    return (
                      <a href="#" className="dropdown-item" onClick={ () => { toggleHiddenColumn(index) } }>
                        <FontAwesomeIcon icon={hiddenColumnSet.has(index) ? faSquare : faCheckSquare} />
                        <span style={{ marginLeft: "0.5em" }}>{ columnNames[index] }</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          { selectedSetSlugs.length > 0 && (
            <div className="level-item">
              <div className="dropdown is-right is-hoverable">
                <div className="dropdown-trigger">
                  <button className="button is-small" aria-haspopup="true" aria-controls="dropdown-menu-parameters">
                    <p className="subtitle is-6">{ selectedSetSlugs.length } selected { beamlineContext.setNoun }{selectedSetSlugs.length > 1 ? 's' : ''}</p>
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faAngleDown} />
                    </span>
                  </button>
                </div>
                <div className="dropdown-menu" id="dropdown-menu-parameters" role="menu">
                  <div className="dropdown-content">
                    <DeleteSets selection={selectedSetSlugs} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="level-item">
              <p className="subtitle is-5"><Link to={ "labels" }>Printable Labels</Link></p>
          </div>
        </div>
      </nav>

      <div className="block">
        { sortedSetSlugs.length == 0 ? (
          <p>( Use the Add button to add { beamlineContext.setNounPlural }. )</p>
        ) : (
          <SortableTable
              extraClasses="settable"
              rowIdentifiers={ sortedSetSlugs }
              columnCount={ setFieldDisplayOrder.length }
              displayRowSelectionBoxes={ true }
              hiddenColumns={ Array.from(hiddenColumnSet) }
              tableFunctions={ sortableTableFunctions }
            />
        )}
      </div>
    </>
  )
}

export default EsafHome
